/**
 * The atlas's own `HitTester` for `@realitycollective/webxr-interactions`.
 *
 * The interaction runtime never touches an engine: it asks its `HitTester`
 * "what does this ray/point touch?" and gets back an interactable id, a
 * distance and a point. This is the one place that answer is computed for the
 * atlas, and it is the same computation `systems/pick.ts` used to do inline:
 * a `Raycaster` into the scene, reading `BatchedMesh.raycast`'s `batchId`
 * back into a part through `BatchedAtlas.partAt`, nearest hit wins.
 *
 * The raycast root is a parameter, not always `AtlasScene.root`: the solo
 * body's root is parented under the grab anchor while comparison bodies stay
 * direct children of `AtlasScene.root` (`src/index.ts`'s `attachSoloBody`),
 * and the anchor itself is an IWSDK transform entity — IWSDK's own
 * `TransformSystem` reparents an entity's `object3D` back under the active
 * level root every frame if it is ever manually parented under a plain
 * (non-entity) `Object3D` instead, which `AtlasScene.root` is. Raycasting
 * `world.scene` (this file's caller passes it) reaches both without fighting
 * that system; the unit test, which has no such split, defaults to the fake
 * scene's own root.
 *
 * `hitRay` serves controller rays, hand pinch-rays, the gaze ray and the desk
 * mouse (`systems/pick.ts`) — one path for all of them, because a
 * `Raycaster` does not care which kind of input aimed it. `hitProximity`
 * serves poke (a fingertip close to a part): rather than raycasting, it
 * measures the query point against each visible part's own bounding box from
 * the catalogue, in the part's body-local space, so it costs one box-distance
 * test per visible part rather than a geometry query.
 *
 * Both methods report the **body id** as the interactable id — not
 * `${bodyId}/${partId}` — because that is what `src/index.ts` registers on
 * the interaction runtime: measured with three bodies compared (6,702
 * parts), `interactions.runtime.update()` cost 4.1 ms against a 0.5 ms
 * budget with one interactable per part (`test/desktop-ux.mjs` §6b), so the
 * client registers one interactable per **body** instead and resolves the
 * specific part from `lastHit()`. See `README.md`'s "Moving the body" for
 * the measured number and `src/index.ts`'s `registerBodyInteractables`.
 *
 * `lastHit()` is the fine-grained answer (`${bodyId}/${partId}`) either
 * method actually found, cached at query time. Reading it from an
 * `runtime.onEvent` listener is reliable *because* the runtime calls
 * `hitRay`/`hitProximity` synchronously inside `resolveTarget`, immediately
 * before routing that same source's hover/press transitions — nothing else
 * queries this hit tester in between, so by the time a listener sees the
 * event, `lastHit()` still names the part that specific query resolved to
 * (`src/index.ts`'s `onInteractionEvent`/`setHoveredInteractable`).
 *
 * No hover throttle lives here: the runtime calls `hitRay`/`hitProximity`
 * once per live source per frame (a handful of calls, not one per part).
 */
import { Box3, Matrix4, Raycaster, Vector3, type BatchedMesh, type Intersection, type Object3D } from "three";
import type { HitTester, InteractableHit } from "@realitycollective/iwsdk-interactions";
import type { RayTuple, Vec3Tuple } from "@realitycollective/iwsdk-interactions";
import type { AtlasBody, AtlasScene } from "../content/atlas-scene.js";

/** How far a ray is allowed to reach; matches `systems/pick.ts`'s prior constant. */
const RAY_FAR = 20;

interface BatchedIntersection extends Intersection {
  batchId?: number;
}

export class AtlasHitTester implements HitTester {
  private readonly raycaster = new Raycaster();
  private readonly origin = new Vector3();
  private readonly direction = new Vector3();
  private readonly hits: Intersection[] = [];
  private readonly worldPoint = new Vector3();
  private readonly localPoint = new Vector3();
  private readonly inverseWorld = new Matrix4();
  private readonly box = new Box3();

  /**
   * The fine-grained hit either method resolved this query (or `null` for a
   * clean miss): `interactableId` here is always `${bodyId}/${partId}`,
   * regardless of what `hitRay`/`hitProximity` themselves return to the
   * runtime. This is the recovery seam `registerBodyInteractables`'s
   * one-per-body registration needs.
   */
  private last: InteractableHit | null = null;

  /** Ray queries answered and the time they took since `takeStats` last ran (the perf monitor reads these). */
  private rayCalls = 0;
  private rayMs = 0;
  /**
   * The last few rays asked about and how far each reached, most recent
   * first, so the pointer visual can end its line where the runtime's own
   * query for that same ray landed (`lastAnswerFor`).
   */
  private readonly recent: { ray: RayTuple; distance: number | null }[] = [];

  constructor(
    private readonly scene: AtlasScene,
    /**
     * Kept for callers that pass it (the unit test's fake scene has no per-body
     * roots); the ray walks each body's own root when a body has one, so hands,
     * controllers, the windows and the grid are never raycast.
     */
    private readonly raycastRoot: Object3D = scene.root as unknown as Object3D,
  ) {
    // three-mesh-bvh honours this: one hit per instance, the nearest, instead of
    // every triangle the ray crosses inside a part.
    (this.raycaster as Raycaster & { firstHitOnly?: boolean }).firstHitOnly = true;
  }

  lastHit(): InteractableHit | null {
    return this.last;
  }

  /**
   * How far the most recent query for this ray reached: a distance, `null` for
   * a miss, `undefined` when this ray has not been asked about. Rays match
   * when every component is within a millionth, which is the same tuple the
   * runtime sampled and passed to `hitRay` this frame.
   */
  lastAnswerFor(ray: RayTuple): number | null | undefined {
    const e = 1e-6;
    for (const entry of this.recent) {
      const r = entry.ray;
      if (
        Math.abs(r.origin[0] - ray.origin[0]) < e &&
        Math.abs(r.origin[1] - ray.origin[1]) < e &&
        Math.abs(r.origin[2] - ray.origin[2]) < e &&
        Math.abs(r.direction[0] - ray.direction[0]) < e &&
        Math.abs(r.direction[1] - ray.direction[1]) < e &&
        Math.abs(r.direction[2] - ray.direction[2]) < e
      )
        return entry.distance;
    }
    return undefined;
  }

  private remember(ray: RayTuple, distance: number | null): void {
    this.recent.unshift({ ray: { origin: [...ray.origin] as RayTuple["origin"], direction: [...ray.direction] as RayTuple["direction"] }, distance });
    if (this.recent.length > 8) this.recent.length = 8;
  }

  /** Ray queries since the last call and their total cost in milliseconds, then reset. */
  takeStats(): { rayCalls: number; rayMs: number } {
    const out = { rayCalls: this.rayCalls, rayMs: +this.rayMs.toFixed(2) };
    this.rayCalls = 0;
    this.rayMs = 0;
    return out;
  }

  hitRay(ray: RayTuple): InteractableHit | null {
    this.last = null;
    if (this.scene.bodies.size === 0) return null;
    this.origin.set(ray.origin[0], ray.origin[1], ray.origin[2]);
    this.direction.set(ray.direction[0], ray.direction[1], ray.direction[2]);
    this.raycaster.set(this.origin, this.direction);
    this.raycaster.far = RAY_FAR;
    this.hits.length = 0;
    const started = performance.now();
    // Each body's own root, not the world: the world also holds the hands, the
    // controllers, five windows and the grid, none of which a part query needs.
    let walked = false;
    for (const body of this.scene.bodies.values()) {
      const root = body.root as unknown as Object3D | undefined;
      if (!root) continue;
      this.raycaster.intersectObject(root, true, this.hits);
      walked = true;
    }
    if (!walked) this.raycaster.intersectObject(this.raycastRoot, true, this.hits);
    else this.hits.sort((a, b) => a.distance - b.distance);
    this.rayCalls += 1;
    this.rayMs += performance.now() - started;
    // sorted by distance, so the first resolvable hit is the nearest
    for (const hit of this.hits) {
      const batchId = (hit as BatchedIntersection).batchId;
      if (batchId === undefined) continue; // the invisible grab hull or another non-batched node
      const mesh = hit.object as unknown as BatchedMesh;
      // `partAt` is keyed by mesh, and each copy owns its own meshes, so the
      // body that resolves the hit is the body the ray actually struck.
      for (const body of this.scene.bodies.values()) {
        const part = body.atlas.partAt(mesh, batchId);
        if (!part) continue;
        // The skin covers everything, so while anything solid shows it is
        // drawn through, never picked: the ray goes on to the part beneath
        // (the source viewer's rule, docs/03-DESIGN.md §5). Alone, it is
        // what there is to pick.
        if (this.seeThrough(body, part.record.system)) break;
        const point: [number, number, number] = [hit.point.x, hit.point.y, hit.point.z];
        this.last = { interactableId: `${body.id}/${part.record.id}`, distance: hit.distance, point };
        this.remember(ray, hit.distance);
        return { interactableId: body.id, distance: hit.distance, point };
      }
    }
    this.remember(ray, null);
    return null;
  }

  /**
   * The nearest visible part whose catalogue bounding box is within `radius`
   * of `point`, across every body. The query point is world space; each
   * body's box test happens in that body's own local space (the query point
   * transformed by the inverse of `body.root.matrixWorld`), because the
   * catalogue's bounds are authored once, unscaled and unposed, for every
   * copy of the atlas.
   */
  /**
   * True when a hit on `systemId` should be looked through: the system is
   * translucent (the skin) and the body is showing at least one opaque system
   * behind it. A body showing only translucent systems keeps them pickable.
   */
  private seeThrough(body: AtlasBody, systemId: string): boolean {
    const systems = this.scene.catalog.systemById;
    if (systems.get(systemId)?.material !== "translucent") return false;
    for (const id of body.visibility.systems) if (systems.get(id)?.material !== "translucent") return true;
    return false;
  }

  hitProximity(point: Vec3Tuple, radius: number): InteractableHit | null {
    this.last = null;
    let bestBodyId: string | null = null;
    let bestPartId: string | null = null;
    let bestDistance = Infinity;
    this.worldPoint.set(point[0], point[1], point[2]);
    const parts = this.scene.catalog.parts;
    for (const body of this.scene.bodies.values()) {
      const mask = body.mask;
      if (!mask) continue; // visibility not yet resolved for this body
      this.inverseWorld.copy(body.root.matrixWorld).invert();
      this.localPoint.copy(this.worldPoint).applyMatrix4(this.inverseWorld);
      // matrixWorld carries the body's scale, so distances measured in its
      // local space are the world distance divided by that scale; undo it
      // once here rather than per part.
      const scale = body.transform.scale || 1;
      for (let slot = 0; slot < parts.length; slot++) {
        if (mask[slot] !== 1) continue;
        const part = parts[slot];
        if (!part) continue;
        if (this.seeThrough(body, part.system)) continue;
        const [min, max] = part.bounds;
        this.box.min.set(min[0] ?? 0, min[1] ?? 0, min[2] ?? 0);
        this.box.max.set(max[0] ?? 0, max[1] ?? 0, max[2] ?? 0);
        const distance = this.box.distanceToPoint(this.localPoint) * scale;
        if (distance <= radius && distance < bestDistance) {
          bestDistance = distance;
          bestBodyId = body.id;
          bestPartId = part.id;
        }
      }
    }
    if (bestBodyId === null || bestPartId === null) return null;
    this.last = { interactableId: `${bestBodyId}/${bestPartId}`, distance: bestDistance, point };
    return { interactableId: bestBodyId, distance: bestDistance, point };
  }
}
